<!-- One chip, two elements. The shared classes are repeated rather than hoisted into a wrapper: a wrapper
     around an inline-flex chip is a second inline box that breaks the `align-top` correction the chip needs
     beside larger text, and a class binding that has to be read in two places to know what is painted is the
     thing that drifts. Both variants are `whitespace-nowrap` + `align-top` + `text-xs` so a table cell keeps
     its row height whether the channel is reachable or not. -->
@if (target(); as quick) {
  <!-- `noopener`: an `https:` target opens a page we do not control, and `window.opener` would let it navigate
       the manager's tab. Harmless on a `tg://` scheme and kept there too, so the two kinds cannot drift. -->
  <a [href]="quick.url" target="_blank" rel="noopener" [title]="tooltip()"
     [attr.data-test]="'contact-chip-' + link().channel" [attr.data-kind]="quick.kind"
     class="inline-flex max-w-full items-center gap-1 align-top whitespace-nowrap rounded-full bg-white px-2 py-0.5
            text-xs font-semibold text-theme-primary-700 shadow-sm ring-1 ring-inset ring-gray-300
            transition-colors hover:bg-gray-50 hover:text-theme-primary-900
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-primary-500">
    <ng-container [ngTemplateOutlet]="icon"></ng-container>
    <span class="sr-only">{{ label() }}:</span>
    <span class="truncate">{{ handle() }}</span>
  </a>
} @else {
  <!-- Consent held, nothing to open. A badge, not an anchor: it takes no tab stop and promises no action. -->
  <span [title]="tooltip()" [attr.data-test]="'contact-chip-' + link().channel" data-kind="none"
        class="inline-flex max-w-full items-center gap-1 align-top whitespace-nowrap rounded-full bg-gray-100
               px-2 py-0.5 text-xs font-semibold text-gray-700">
    <ng-container [ngTemplateOutlet]="icon"></ng-container>
    <span class="sr-only">{{ label() }}:</span>
    <span class="truncate">{{ handle() }}</span>
  </span>
}

<!-- The channel glyph. `aria-hidden`, because it repeats the sr-only channel name in a form no screen reader
     can read; explicitly sized, because an <svg> carrying only a viewBox collapses to 0x0 in WebKit (Bug #218);
     and `fill="currentColor"` on the path itself so a hover colour change carries the icon with the label. -->
<ng-template #icon>
  @switch (link().channel) {
    @case ('telegram') {
      <svg class="h-3.5 w-3.5 shrink-0" viewBox="0 0 16 16" aria-hidden="true"
           xmlns="http://www.w3.org/2000/svg">
        <path fill="currentColor"
              d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8.287 5.906c-.778.324-2.334.994-4.666 2.01-.378.15-.577.298-.595.442-.03.243.275.339.69.47l.175.055c.408.133.958.288 1.243.294.26.006.549-.1.868-.32 2.179-1.471 3.304-2.214 3.374-2.23.05-.012.12-.026.166.016.047.041.042.12.037.141-.03.129-1.227 1.241-1.846 1.817-.193.18-.33.307-.358.336a8.154 8.154 0 0 1-.188.186c-.38.366-.664.64.015 1.088.327.216.589.393.85.571.284.194.568.387.936.629.093.06.183.125.27.187.331.236.63.448.997.414.214-.02.435-.22.547-.82.265-1.417.786-4.486.906-5.751a1.426 1.426 0 0 0-.013-.315.337.337 0 0 0-.114-.217.526.526 0 0 0-.31-.093c-.3.005-.763.166-2.984 1.09z"/>
      </svg>
    }
    @default {
      <!-- A channel this build has never heard of still gets a chip: the handle and the link are the useful
           half, and a generic speech bubble is a better answer than an empty box or a crash. -->
      <svg class="h-3.5 w-3.5 shrink-0" viewBox="0 0 16 16" aria-hidden="true"
           xmlns="http://www.w3.org/2000/svg">
        <path fill="currentColor"
              d="M8 15a6.97 6.97 0 0 1-3.106-.723L1.5 15l.723-3.394A6.97 6.97 0 0 1 1 8c0-3.866 3.134-7 7-7s7 3.134 7 7-3.134 7-7 7zm0-1.5A5.5 5.5 0 1 0 8 2.5a5.5 5.5 0 0 0 0 11z"/>
      </svg>
    }
  }
</ng-template>
